<template>
  <div class="cashback-listagem">
    <Loading :isActive="loading && !jaCarregou" />

    <ModalRegraCashback :item="currentItem" :close="closeEdit" />

    <div class="cashback-listagem__header">
      <CButton color="primary" @click="openCreate">
        <CIcon name="cil-plus" size="sm" class="mr-1" />
        Nova regra
      </CButton>
    </div>

    <div class="cashback-listagem__filtros">
      <CRow>
        <CCol md="8">
          <div class="cashback-listagem__busca">
            <input
              class="form-control"
              type="text"
              v-model="filtros.termo"
              placeholder="Buscar por nome ou descrição..."
              aria-label="Buscar regra por nome ou descrição"
              @input="loadRegrasComDebounce()"
            />
            <fa-icon v-if="loading && jaCarregou" icon="spinner" spin class="cashback-listagem__busca-indicador" />
          </div>
        </CCol>
        <CCol md="4">
          <select class="form-control" v-model="filtros.ativo" aria-label="Situação da regra" @change="loadRegras()">
            <option value="">Situação: Todas</option>
            <option value="1">Ativas</option>
            <option value="0">Inativas</option>
          </select>
        </CCol>
      </CRow>
    </div>

    <div :class="['cashback-listagem__table-wrapper', { 'cashback-listagem__table-wrapper--atualizando': loading && jaCarregou }]">
      <table class="cashback-listagem__table">
        <thead>
          <tr>
            <th class="cashback-listagem__col-id">ID</th>
            <th>Nome</th>
            <th class="cashback-listagem__col-prioridade">Prioridade</th>
            <th>Faixas</th>
            <th>Canais</th>
            <th class="cashback-listagem__col-vigencia">Vigência</th>
            <th class="cashback-listagem__col-ativo">Ativa</th>
            <th class="cashback-listagem__col-acoes"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="erroCarga">
            <td colspan="8" class="cashback-listagem__empty">
              <div class="cashback-listagem__erro">Não foi possível carregar as regras: {{ erroCarga }}</div>
              <CButton color="primary" variant="outline" size="sm" @click="loadRegras(paginaAtual)">Tentar novamente</CButton>
            </td>
          </tr>
          <tr v-else-if="regras.length === 0">
            <td colspan="8" class="cashback-listagem__empty">Nenhuma regra de cashback cadastrada</td>
          </tr>
          <tr v-for="regra in regras" :key="regra.id" class="cashback-listagem__row">
            <td class="cashback-listagem__col-id">{{ regra.id }}</td>
            <td><strong>{{ regra.nome }}</strong></td>
            <td class="cashback-listagem__col-prioridade">{{ regra.prioridade }}</td>
            <td>
              <div
                v-if="(regra.faixas || []).length"
                class="cashback-listagem__faixas"
                v-c-tooltip="{ content: detalheFaixas(regra), html: true, placement: 'top', boundaries: 'window' }"
              >
                <span class="cashback-listagem__faixas-retorno">{{ retornoFaixas(regra) }}</span>
                <span class="cashback-listagem__faixas-apoio">{{ apoioFaixas(regra) }}</span>
              </div>
              <span v-else class="cashback-listagem__faixas-apoio">—</span>
            </td>
            <td>
              <span v-for="canal in regra.canais" :key="canal" class="cashback-badge cashback-badge--canal">
                <img v-if="imagemCanal(canal)" :src="imagemCanal(canal)" alt="" class="cashback-badge__logo" />
                {{ labelCanal(canal) }}
              </span>
            </td>
            <td class="cashback-listagem__col-vigencia">{{ vigencia(regra) }}</td>
            <td class="cashback-listagem__col-ativo">
              <span :class="['cashback-badge', regra.ativo ? 'cashback-badge--ativo' : 'cashback-badge--inativo']">
                {{ regra.ativo ? "Sim" : "Não" }}
              </span>
            </td>
            <td class="cashback-listagem__col-acoes">
              <CButton
                size="sm"
                color="secondary"
                class="cashback-listagem__botao-acao"
                title="Duplicar"
                :aria-label="`Duplicar a regra ${regra.nome}`"
                @click="duplicar(regra)"
              >
                <fa-icon icon="clone" />
              </CButton>
              <CButton
                size="sm"
                color="primary"
                class="cashback-listagem__botao-acao"
                title="Alterar"
                :aria-label="`Alterar a regra ${regra.nome}`"
                @click="editar(regra)"
              >
                <fa-icon icon="pencil-alt" />
              </CButton>
              <CButton
                size="sm"
                color="danger"
                class="cashback-listagem__botao-acao"
                title="Excluir"
                :aria-label="`Excluir a regra ${regra.nome}`"
                @click="excluir(regra)"
              >
                <fa-icon icon="trash-alt" />
              </CButton>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="cashback-listagem__pagination">
      <pagination :data="paginator" :limit="5" @pagination-change-page="loadRegras" />
    </div>
  </div>
</template>

<script>
import clone from "just-clone";
import _debounce from "lodash/debounce";
import { get, del } from "@/helpers/apiRequest";
import Loading from "@/components/Loading.vue";
import ModalRegraCashback from "./ModalRegraCashback/ModalRegraCashback.vue";
import { imagemCanal } from "./canais";
import {
  DEBOUNCE_BUSCA_MS,
  ITENS_POR_PAGINA,
  formatData,
  formatMoney,
  formatPercentual,
  mensagemErroApi,
  nomeCopia,
  novaRegra,
  numero,
  ordenarFaixas,
  rotulosPorChave,
  tetoDaFaixa,
} from "./helpers";

export default {
  name: "cashback-regras",
  components: { Loading, ModalRegraCashback },

  data() {
    return {
      loading: false,
      jaCarregou: false,
      erroCarga: null,
      regras: [],
      paginator: {},
      rotulosCanais: {},
      currentItem: novaRegra(),
      filtros: { termo: "", ativo: "" },
    };
  },

  computed: {
    paginaAtual() {
      return this.paginator.current_page || 1;
    },
  },

  methods: {
    imagemCanal,

    loadRegras(page = 1) {
      this.loading = true;
      this.erroCarga = null;
      const sequencia = (this.sequenciaCarga = (this.sequenciaCarga || 0) + 1);
      const params = new URLSearchParams({ page, take: ITENS_POR_PAGINA, q: this.filtros.termo, ativo: this.filtros.ativo });

      return get(`/admin/cashback/regras?${params}`)
        .then((json) => {
          if (sequencia !== this.sequenciaCarga) return;

          this.regras = json.itens || [];
          this.paginator = json.paginator || {};
        })
        .catch((err) => {
          if (sequencia !== this.sequenciaCarga) return;

          this.regras = [];
          this.erroCarga = mensagemErroApi(err);
        })
        .finally(() => {
          if (sequencia !== this.sequenciaCarga) return;

          this.loading = false;
          this.jaCarregou = true;
        });
    },

    loadRegrasComDebounce: _debounce(function () {
      this.loadRegras();
    }, DEBOUNCE_BUSCA_MS),

    loadRotulosCanais() {
      return get("/admin/cashback/regras/opcoes")
        .then((json) => {
          this.rotulosCanais = rotulosPorChave(json && json.canais);
        })
        .catch(() => {
          this.rotulosCanais = {};
        });
    },

    openCreate() {
      this.currentItem = novaRegra();
      this.$modal.show("ModalRegraCashback");
    },

    editar(regra) {
      this.currentItem = clone(regra);
      this.$modal.show("ModalRegraCashback");
    },

    duplicar(regra) {
      const copia = clone(regra);
      copia.id = null;
      copia.ativo = false;
      copia.nome = nomeCopia(copia.nome);
      copia.faixas = (copia.faixas || []).map((faixa) => ({ ...faixa, id: null }));
      copia.restricoes = (copia.restricoes || []).map((restricao) => ({ ...restricao, id: null }));
      this.currentItem = copia;
      this.$modal.show("ModalRegraCashback");
    },

    closeEdit(salvou) {
      this.$modal.hide("ModalRegraCashback");

      if (salvou) this.loadRegras(this.paginaAtual);
    },

    excluir(regra) {
      this.$swal({
        title: "Excluir a regra de cashback?",
        html: "<p>Pedidos novos deixam de gerar cashback por esta regra. O cashback já confirmado na carteira dos clientes não é alterado.</p>",
        showCancelButton: true,
        icon: "warning",
        confirmButtonText: "Excluir",
        cancelButtonText: "Cancelar",
      })
        .then((res) => {
          if (!res.value) return;

          this.loading = true;
          del(`/admin/cashback/regras/${regra.id}`)
            .then(() => this.loadRegras(this.regras.length === 1 && this.paginaAtual > 1 ? this.paginaAtual - 1 : this.paginaAtual))
            .catch((err) => this.$swal({ title: "Falha ao excluir", text: mensagemErroApi(err), icon: "error" }))
            .finally(() => {
              this.loading = false;
            });
        })
        .catch(() => {});
    },

    labelCanal(canal) {
      return this.rotulosCanais[canal] || canal;
    },

    retornoFaixas(regra) {
      const percentuais = (regra.faixas || []).map((faixa) => numero(faixa.percentual)).filter((valor) => valor > 0);

      if (!percentuais.length) return formatPercentual(0);

      const menor = Math.min(...percentuais);
      const maior = Math.max(...percentuais);

      return menor === maior ? formatPercentual(menor) : `${formatPercentual(menor)} a ${formatPercentual(maior)}`;
    },

    apoioFaixas(regra) {
      const faixas = ordenarFaixas(regra.faixas);
      const quantidade = faixas.length === 1 ? "1 faixa" : `${faixas.length} faixas`;

      return `${quantidade} · a partir de ${formatMoney(faixas[0].valorMinimo)}`;
    },

    detalheFaixas(regra) {
      return ordenarFaixas(regra.faixas)
        .map((faixa) => {
          const teto = tetoDaFaixa(faixa);
          const intervalo = teto !== null ? `${formatMoney(faixa.valorMinimo)} a ${formatMoney(teto)}` : `A partir de ${formatMoney(faixa.valorMinimo)}`;
          const retorno = numero(faixa.percentual) > 0 ? formatPercentual(faixa.percentual) : "sem cashback";

          return `<div class="cashback-listagem__faixa-detalhe">${intervalo} → <strong>${retorno}</strong></div>`;
        })
        .join("");
    },

    vigencia(regra) {
      if (!regra.dataInicio && !regra.dataFim) return "Sem prazo";

      return `${formatData(regra.dataInicio, "…")} até ${formatData(regra.dataFim, "…")}`;
    },
  },

  beforeMount() {
    this.loadRegras();
    this.loadRotulosCanais();
  },
};
</script>

<style scoped>
.cashback-listagem__header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.cashback-listagem__filtros {
  margin-bottom: 20px;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
}

.cashback-listagem__busca {
  position: relative;
}

.cashback-listagem__busca .form-control {
  padding-right: 34px;
}

.cashback-listagem__busca-indicador {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  color: #98a2b3;
  pointer-events: none;
}

.cashback-listagem__table-wrapper--atualizando {
  opacity: 0.55;
  pointer-events: none;
}

.cashback-listagem__table-wrapper {
  transition: opacity 0.15s ease;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  overflow: clip;
  background: #fff;
  margin-bottom: 20px;
}

.cashback-listagem__table {
  width: 100%;
  border-collapse: collapse;
}

.cashback-listagem__table thead tr {
  background: #f9fafb;
  border-bottom: 1px solid #e4e7eb;
}

.cashback-listagem__table th {
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 12px;
  white-space: nowrap;
}

.cashback-listagem__table td {
  padding: 12px;
  border-bottom: 1px solid #f2f4f7;
  font-size: 13px;
  color: #344054;
  vertical-align: middle;
}

.cashback-listagem__row:hover {
  background: #f9fafb;
}

.cashback-listagem__table td.cashback-listagem__empty {
  text-align: center;
  padding: 48px 20px;
  color: #98a2b3;
}

.cashback-listagem__erro {
  margin-bottom: 10px;
  color: #b42318;
}

.cashback-listagem__pagination {
  display: flex;
  justify-content: center;
}

.cashback-listagem__faixas {
  display: inline-flex;
  flex-direction: column;
  cursor: help;
}

.cashback-listagem__faixas-retorno {
  font-weight: 600;
  color: #101828;
}

.cashback-listagem__faixas-apoio {
  font-size: 11.5px;
  color: #98a2b3;
}

.cashback-badge {
  display: inline-block;
  margin: 1px 2px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: #eef2ff;
  color: var(--cashback-cor-primaria);
  border: 1px solid #dfe3ff;
}

.cashback-badge--canal {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px 3px 4px;
  border-radius: 999px;
  background: #ffffff;
  border-color: #e4e7ec;
  color: #344054;
}

.cashback-badge__logo {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.cashback-badge--ativo {
  background: #e8f5e9;
  color: #2e7d32;
  border-color: #c8e6c9;
}

.cashback-badge--inativo {
  background: #f5f5f5;
  color: #999;
  border-color: #e0e0e0;
}

.cashback-listagem__col-id { width: 60px; text-align: center; }
.cashback-listagem__col-prioridade { width: 90px; text-align: center; }
.cashback-listagem__col-vigencia { width: 190px; }
.cashback-listagem__col-ativo { width: 70px; text-align: center; }
.cashback-listagem__col-acoes { width: 130px; text-align: center; white-space: nowrap; }
.cashback-listagem__botao-acao { margin: 0 2px; }
</style>
